import { useState, useEffect, useCallback, useMemo } from 'react';
import { AppMode } from '../types';

export type ResponsiveMode = 'current' | 'container' | 'smart' | 'dynamic' | 'height' | 'adaptive';

interface DeviceInfo {
  viewport: { width: number; height: number };
  screen: { width: number; height: number };
  devicePixelRatio: number;
  orientation: number;
  orientationType: string;
  touchSupport: boolean;
  userAgent: string;
  isPortrait: boolean;
  aspectRatio: string;
}

interface DeviceCategory {
  category: string;
  icon: string;
  optimalColumns: number;
  measureSize: string;
  features: string;
}

export interface UseResponsiveLayoutReturn {
  responsiveMode: ResponsiveMode;
  setResponsiveMode: React.Dispatch<React.SetStateAction<ResponsiveMode>>;
  viewportHeight: number;
  measuredLayout: { columns: number; rows: number; customStyle?: React.CSSProperties } | null;
  setMeasuredLayout: React.Dispatch<React.SetStateAction<{ columns: number; rows: number; customStyle?: React.CSSProperties } | null>>;
  deviceInfo: DeviceInfo;
  deviceCategory: DeviceCategory;
  getDeviceCategory: () => DeviceCategory;
  getResponsiveGridClasses: (chartMode: AppMode, numberOfMeasures: number) => string;
  getHeightBasedGridStyle: () => React.CSSProperties | undefined;
  calculateFixed4ColumnLayout: (numberOfMeasures: number, availableHeight: number) => { columns: number; rows: number; measureSize: number; allowScroll: boolean };
  measureHeightBasedLayout: (numberOfMeasures: number, chartMode: AppMode) => Promise<{ columns: number; rows: number; customStyle?: React.CSSProperties } | null>;
}

export function useResponsiveLayout(
  numberOfMeasures: number,
  isGigModeView: boolean,
  mode: AppMode,
): UseResponsiveLayoutReturn {
  const [responsiveMode, setResponsiveMode] = useState<ResponsiveMode>('adaptive');
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [measuredLayout, setMeasuredLayout] = useState<{ columns: number; rows: number; customStyle?: React.CSSProperties } | null>(null);

  // Device detection state with adaptive layout intelligence
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: window.screen?.width || 0, height: window.screen?.height || 0 },
    devicePixelRatio: window.devicePixelRatio || 1,
    orientation: window.screen?.orientation?.angle || 0,
    orientationType: window.screen?.orientation?.type || 'unknown',
    touchSupport: 'ontouchstart' in window,
    userAgent: navigator.userAgent,
    isPortrait: window.innerHeight > window.innerWidth,
    aspectRatio: (window.innerWidth / window.innerHeight).toFixed(2)
  });

  // Intelligent device categorization
  const getDeviceCategory = useCallback((): DeviceCategory => {
    const width = deviceInfo.viewport.width;
    const height = deviceInfo.viewport.height;
    const touch = deviceInfo.touchSupport;

    if (width <= 480 || (touch && Math.max(width, height) <= 896)) {
      return {
        category: 'mobile',
        icon: '📱',
        optimalColumns: deviceInfo.isPortrait ? 1 : 2,
        measureSize: 'large',
        features: 'simplified'
      };
    }

    if (width <= 1024 || (touch && Math.max(width, height) <= 1366)) {
      return {
        category: 'tablet',
        icon: '📋',
        optimalColumns: deviceInfo.isPortrait ? 2 : 4,
        measureSize: 'medium',
        features: 'balanced'
      };
    }

    return {
      category: 'desktop',
      icon: '🖥️',
      optimalColumns: 4,
      measureSize: width >= 2560 ? 'small' : 'medium',
      features: 'full'
    };
  }, [deviceInfo]);

  const deviceCategory = useMemo(() => getDeviceCategory(), [getDeviceCategory]);

  // Viewport tracking for height-responsive mode
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);

      setDeviceInfo({
        viewport: { width: window.innerWidth, height: window.innerHeight },
        screen: { width: window.screen?.width || 0, height: window.screen?.height || 0 },
        devicePixelRatio: window.devicePixelRatio || 1,
        orientation: window.screen?.orientation?.angle || 0,
        orientationType: window.screen?.orientation?.type || 'unknown',
        touchSupport: 'ontouchstart' in window,
        userAgent: navigator.userAgent,
        isPortrait: window.innerHeight > window.innerWidth,
        aspectRatio: (window.innerWidth / window.innerHeight).toFixed(2)
      });
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const calculateFixed4ColumnLayout = useCallback((numberOfMeasures: number, availableHeight: number): { columns: number; rows: number; measureSize: number; allowScroll: boolean } => {
    const FIXED_COLUMNS = 4;
    const MIN_MEASURE_SIZE = 80;

    const totalSlots = Math.ceil(numberOfMeasures / FIXED_COLUMNS) * FIXED_COLUMNS;
    const actualRows = Math.ceil(totalSlots / FIXED_COLUMNS);

    const GAP_SIZE = 16;
    const totalGapHeight = (actualRows - 1) * GAP_SIZE;
    const availableForMeasures = availableHeight - totalGapHeight;
    const calculatedMeasureHeight = availableForMeasures / actualRows;

    console.log(`📐 Height calculation: ${availableHeight}px available - ${totalGapHeight}px gaps = ${availableForMeasures}px ÷ ${actualRows} rows = ${calculatedMeasureHeight.toFixed(1)}px per measure`);

    if (calculatedMeasureHeight < MIN_MEASURE_SIZE) {
      console.log(`⚠️ Calculated height ${calculatedMeasureHeight.toFixed(0)}px < minimum ${MIN_MEASURE_SIZE}px - allowing scroll`);
      return {
        columns: FIXED_COLUMNS,
        rows: actualRows,
        measureSize: MIN_MEASURE_SIZE,
        allowScroll: true
      };
    }

    console.log(`✅ Height-based layout: ${FIXED_COLUMNS} columns, ${actualRows} rows, ${calculatedMeasureHeight.toFixed(0)}px height`);
    console.log(`🎼 Slots: ${numberOfMeasures} measures → ${totalSlots} slots (${totalSlots - numberOfMeasures} empty)`);

    return {
      columns: FIXED_COLUMNS,
      rows: actualRows,
      measureSize: calculatedMeasureHeight,
      allowScroll: false
    };
  }, []);

  const measureHeightBasedLayout = useCallback(async (numberOfMeasures: number, chartMode: AppMode): Promise<{ columns: number; rows: number; customStyle?: React.CSSProperties } | null> => {
    const headerHeight = chartMode === 'gig' ? 80 : 150;
    const controlsHeight = chartMode === 'gig' ? 40 : 100;
    const paddingHeight = 60;

    const availableHeight = viewportHeight - headerHeight - controlsHeight - paddingHeight;

    console.log(`📐 Available height for fixed 4-column layout: ${availableHeight.toFixed(1)}px`);

    const layout = calculateFixed4ColumnLayout(numberOfMeasures, availableHeight);

    const customStyle: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: `repeat(${layout.columns}, 1fr)`,
      gap: '16px',
      width: '100%',
      height: layout.allowScroll ? 'auto' : 'fit-content',
      overflow: layout.allowScroll ? 'auto' : 'visible'
    };

    if (layout.measureSize !== 80) {
      (customStyle as any)['--measure-size'] = `${layout.measureSize}px`;
    }

    console.log(`🎯 Fixed 4-column layout: ${layout.columns}×${layout.rows}, ${layout.measureSize.toFixed(0)}px squares${layout.allowScroll ? ' (scroll enabled)' : ''}`);

    return {
      columns: layout.columns,
      rows: layout.rows,
      customStyle
    };
  }, [viewportHeight, calculateFixed4ColumnLayout]);

  // Trigger measurement when height mode is selected
  useEffect(() => {
    if (responsiveMode === 'height') {
      const triggerMeasurement = async () => {
        const layout = await measureHeightBasedLayout(numberOfMeasures, isGigModeView ? 'gig' : mode);
        if (layout) {
          setMeasuredLayout(layout);
        }
      };
      triggerMeasurement();
    } else {
      setMeasuredLayout(null);
    }
  }, [responsiveMode, numberOfMeasures, isGigModeView, mode, measureHeightBasedLayout, viewportHeight]);

  // Device-adaptive responsive grid classes
  const getAdaptiveGridClasses = useCallback((chartMode: AppMode) => {
    const device = getDeviceCategory();

    if (chartMode === 'gig' || chartMode === 'sandbox') {
      switch (device.category) {
        case 'mobile':
          return deviceInfo.isPortrait
            ? 'grid grid-cols-1 gap-3 p-3'
            : 'grid grid-cols-2 gap-2 p-2';
        case 'tablet':
          return deviceInfo.isPortrait
            ? 'grid grid-cols-2 gap-3 p-3'
            : 'grid grid-cols-4 gap-2 p-2';
        case 'desktop':
        default:
          return 'grid grid-cols-4 gap-2 p-4 max-w-6xl mx-auto';
      }
    } else {
      switch (device.category) {
        case 'mobile':
          return 'grid grid-cols-1 gap-6 p-4 pb-96';
        case 'tablet':
          return deviceInfo.isPortrait
            ? 'grid grid-cols-1 sm:grid-cols-2 gap-5 p-4 pb-96'
            : 'grid grid-cols-2 lg:grid-cols-3 gap-4 p-4 pb-96';
        case 'desktop':
        default:
          return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 pb-96 max-w-6xl mx-auto';
      }
    }
  }, [deviceInfo, getDeviceCategory]);

  // Get responsive grid classes based on current mode
  const getResponsiveGridClasses = useCallback((chartMode: AppMode, numberOfMeasures: number) => {
    if (chartMode === 'gig') {
      switch (responsiveMode) {
        case 'current':
          return 'grid grid-cols-2 md:grid-cols-4 gap-1';
        case 'container':
          return 'grid grid-cols-2 md:grid-cols-4 gap-1 max-w-4xl mx-auto';
        case 'smart':
          return 'grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1';
        case 'dynamic': {
          const optimalCols = numberOfMeasures <= 16 ? 4 : numberOfMeasures <= 32 ? 8 : 12;
          return `grid gap-1 max-w-6xl mx-auto` +
                 ` grid-cols-2 sm:grid-cols-${Math.min(optimalCols, 6)} md:grid-cols-${Math.min(optimalCols, 8)} lg:grid-cols-${Math.min(optimalCols, 12)}`;
        }
        case 'height':
          return 'grid gap-1';
        case 'adaptive':
          return getAdaptiveGridClasses(chartMode);
        default:
          return 'grid grid-cols-2 md:grid-cols-4 gap-1';
      }
    } else {
      switch (responsiveMode) {
        case 'current':
          return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-96';
        case 'container':
          return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-96 max-w-5xl mx-auto';
        case 'smart':
          return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 pb-96';
        case 'dynamic': {
          const editionCols = numberOfMeasures <= 8 ? 2 : numberOfMeasures <= 16 ? 4 : 6;
          return `grid gap-4 pb-96 max-w-6xl mx-auto` +
                 ` grid-cols-1 sm:grid-cols-2 md:grid-cols-${Math.min(editionCols, 4)} lg:grid-cols-${Math.min(editionCols, 6)}`;
        }
        case 'height':
          return 'grid gap-4 pb-96';
        case 'adaptive':
          return getAdaptiveGridClasses(chartMode);
        default:
          return 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-96';
      }
    }
  }, [responsiveMode, getAdaptiveGridClasses]);

  // Get inline grid style for height-responsive mode
  const getHeightBasedGridStyle = useCallback((): React.CSSProperties | undefined => {
    if (responsiveMode === 'height' && measuredLayout) {
      return measuredLayout.customStyle || {
        gridTemplateColumns: `repeat(${measuredLayout.columns}, 1fr)`,
      };
    }
    return undefined;
  }, [responsiveMode, measuredLayout]);

  return {
    responsiveMode,
    setResponsiveMode,
    viewportHeight,
    measuredLayout,
    setMeasuredLayout,
    deviceInfo,
    deviceCategory,
    getDeviceCategory,
    getResponsiveGridClasses,
    getHeightBasedGridStyle,
    calculateFixed4ColumnLayout,
    measureHeightBasedLayout,
  };
}
